import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { FinanceiroService } from './Service';
import { AssinaturasService } from '../assinaturas/estabelecimento/Service';
import { formatMoneyBRL } from './utils';

const financeiro = new FinanceiroService();
const assinaturasService = new AssinaturasService();

const HORIZON_MONTHS = 60;

const getClientName = (row: any) =>
    row?.clientName || row?.clienteNome || row?.cliente?.name || row?.client?.name || 'Cliente não identificado';

const getPlanName = (row: any) =>
    row?.planName || row?.planoNome || row?.plano?.name || row?.plan?.name || row?.planoSnapshot?.name || '-';

const formatDay = (value?: string) => (value ? moment(value).format('DD/MM/YYYY') : '-');

export default function AssinaturasFinanceiro() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [previsao, setPrevisao] = useState<any>(null);
    const [assinaturas, setAssinaturas] = useState<any[]>([]);

    const horizon = useMemo(() => moment().add(HORIZON_MONTHS, 'months').format('YYYY-MM-DD'), []);

    useEffect(() => {
        let active = true;
        setLoading(true);
        Promise.all([
            financeiro.previsaoAssinaturas({ to: horizon }),
            assinaturasService.findAll(),
        ])
            .then(([prev, list]) => {
                if (!active) return;
                setPrevisao(prev?.data || null);
                setAssinaturas(Array.isArray(list?.data) ? list.data : []);
            })
            .catch((e: any) => {
                if (!active) return;
                setError(String(e?.response?.data?.message || 'Erro ao carregar as assinaturas'));
                console.error(e);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [horizon]);

    const nomePorAssinatura = useMemo(() => {
        const map = new Map<string, any>();
        assinaturas.forEach((row) => row?._id && map.set(String(row._id), row));
        return map;
    }, [assinaturas]);

    const porAssinatura: any[] = previsao?.porAssinatura || [];
    const months: any[] = previsao?.months || [];

    return (
        <div className="flex flex-col gap-4">
            {error && (
                <div className="alert alert-error">
                    <span>{error}</span>
                </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Provisão total em aberto</div>
                        <div className="text-2xl font-semibold">{loading ? '...' : formatMoneyBRL(previsao?.total || 0)}</div>
                        <div className="text-xs opacity-70">Todos os períodos ainda não pagos</div>
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Vencido</div>
                        <div className="text-2xl font-semibold">{loading ? '...' : formatMoneyBRL(previsao?.totalVencido || 0)}</div>
                        <div className="text-xs opacity-70">Passou do vencimento sem pagamento</div>
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Meses futuros ainda não gerados</div>
                        <div className="text-2xl font-semibold">{loading ? '...' : formatMoneyBRL(previsao?.totalProjetado || 0)}</div>
                        <div className="text-xs opacity-70">Projeção pela periodicidade, até {HORIZON_MONTHS} meses</div>
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <h2 className="font-medium">Por assinatura</h2>
                    <p className="text-xs opacity-70">Parcelas ainda não pagas de cada assinatura, da mais próxima para a mais distante. "Projetadas" são meses que ainda não foram gerados no sistema.</p>
                    {porAssinatura.length ? (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th>Plano</th>
                                        <th>Próximo vencimento</th>
                                        <th>Parcelas</th>
                                        <th>Projetadas</th>
                                        <th className="text-right">Previsto</th>
                                        <th className="text-right">Vencido</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {porAssinatura.map((item) => {
                                        const row = nomePorAssinatura.get(String(item.assinaturaId));
                                        return (
                                            <tr key={item.assinaturaId}>
                                                <td>{getClientName(row)}</td>
                                                <td>{getPlanName(row)}</td>
                                                <td>{formatDay(item.proximoVencimento)}</td>
                                                <td>{item.quantidade}</td>
                                                <td>{item.projetadas || 0}</td>
                                                <td className="text-right">{formatMoneyBRL(item.previsto)}</td>
                                                <td className="text-right">{formatMoneyBRL(item.vencido)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm opacity-70">{loading ? 'Carregando...' : 'Nenhuma assinatura com parcelas em aberto.'}</p>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <h2 className="font-medium">Por mês de vencimento</h2>
                    {months.length ? (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Mês</th>
                                        <th>Parcelas</th>
                                        <th>Projetadas</th>
                                        <th className="text-right">Previsto</th>
                                        <th className="text-right">Vencido</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {months.map((row) => (
                                        <tr key={row.month}>
                                            <td>{row.month.split('-').reverse().join('/')}</td>
                                            <td>{row.quantidade}</td>
                                            <td>{row.projetadas || 0}</td>
                                            <td className="text-right">{formatMoneyBRL(row.previsto)}</td>
                                            <td className="text-right">{formatMoneyBRL(row.vencido)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm opacity-70">{loading ? 'Carregando...' : 'Nenhuma parcela em aberto.'}</p>
                    )}
                </div>
            </div>
        </div>
    );
}
